import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Database, Loader2, RotateCcw, TriangleAlert } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { Correspondances } from "@/components/migration/Correspondances";
import { Rapport } from "@/components/migration/Rapport";
import { Confirmer } from "@/components/migration/Confirmer";
import { ImportJson } from "@/components/migration/ImportJson";

const SOURCES = [["json", "Fichier d'export JSON noCRM"], ["simulation", "Jeu d'essai (simulation)"]];

export default function MigrationNocrm() {
  const [source, setSource] = useState("json");
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2" data-testid="migration-source-switch">
        {SOURCES.map(([c, l]) => (
          <button key={c} type="button" onClick={() => setSource(c)} className="min-h-[44px] at-pill px-3 text-sm font-semibold" data-testid={`migration-source-${c}`}
            style={{ background: source === c ? "var(--accent)" : "var(--surface-2)", color: source === c ? "var(--accent-ink)" : "var(--ink)" }}>{l}</button>
        ))}
      </div>
      {source === "json" ? <ImportJson /> : <Simulation />}
    </div>
  );
}

const ETATS = { a_configurer: "À configurer", echantillon: "Échantillon importé", complet: "Import complet fait" };

const Source = ({ e }) => (
  <section className="at-card p-5 space-y-3" data-testid="migration-source">
    <div className="flex items-start gap-3">
      <span className="w-11 h-11 shrink-0 at-pill flex items-center justify-center" style={{ background: "var(--surface-2)" }}><Database size={18} /></span>
      <div className="min-w-0">
        <p className="font-bold">Connecteur noCRM</p>
        <p className="text-xs at-muted">Mode simulation : jeu d'essai, aucune clé d'API utilisée.</p>
      </div>
      <span className="ml-auto at-pill px-3 py-1 text-xs font-semibold whitespace-nowrap" style={{ background: "var(--surface-2)" }} data-testid="migration-state">{ETATS[e.etat]}</span>
    </div>
    <div className="grid grid-cols-3 gap-2 text-center">
      {[["fiches", "Opportunités"], ["commentaires", "Commentaires"], ["evenements", "Événements"]].map(([k, l]) => (
        <div key={k} className="at-soft p-3 min-w-0"><p className="text-lg font-bold" data-testid={`migration-source-${k}`}>{e.source[k]}</p><p className="text-[10px] at-muted break-words">{l}</p></div>
      ))}
    </div>
    <p className="text-xs at-muted" data-testid="migration-imported-count">{e.importees} fiche(s) importée(s) actuellement dans le CRM.</p>
  </section>
);

const Etape = ({ n, titre, children, testId }) => (
  <section className="at-card p-5 space-y-3" data-testid={testId}>
    <p className="font-bold flex items-center gap-2"><span className="w-7 h-7 at-pill flex items-center justify-center text-sm" style={{ background: "var(--accent)", color: "var(--accent-ink)" }}>{n}</span>{titre}</p>
    {children}
  </section>
);

function Simulation() {
  const [e, setE] = useState(null);
  const [occupe, setOccupe] = useState(null);
  const charger = useCallback(() => api.get("/migration-nocrm").then((r) => setE(r.data)).catch((x) => toast.error(messageErreur(x))), []);
  useEffect(() => { charger(); }, [charger]);
  const lancer = async (cle, url, corps, ok) => {
    setOccupe(cle);
    try { const r = await api.post(url, corps); toast.success(r.data.message || ok); await charger(); }
    catch (x) { toast.error(messageErreur(x)); }
    finally { setOccupe(null); }
  };
  if (!e) return <p className="at-muted text-sm" data-testid="migration-loading">Chargement…</p>;
  const bloque = e.manquantes.length > 0;
  const Bouton = ({ cle, url, ok, children, variante = "at-btn-primary", disabled }) => (
    <button type="button" disabled={disabled || !!occupe} onClick={() => lancer(cle, url, {}, ok)} className={`at-btn ${variante} w-full`} data-testid={`migration-${cle}-button`}>
      {occupe === cle ? <Loader2 size={16} className="animate-spin" /> : null}{children}
    </button>
  );
  return (
    <div className="space-y-4" data-testid="migration-nocrm">
      <Source e={e} />
      <Etape n="1" titre="Correspondances" testId="migration-step-1">
        <Correspondances e={e} onEnregistre={setE} />
      </Etape>
      <Etape n="2" titre="Échantillon de 50 fiches" testId="migration-step-2">
        {bloque && <p className="text-sm flex gap-2" style={{ color: "var(--danger)" }} data-testid="migration-blocked"><TriangleAlert size={16} className="shrink-0 mt-0.5" />Import bloqué : {e.manquantes.length} étape(s) noCRM sans correspondance.</p>}
        <Bouton cle="sample" url="/migration-nocrm/echantillon" ok="Échantillon importé." disabled={bloque || e.etat === "complet"}>Importer l'échantillon<ArrowRight size={16} /></Bouton>
        {e.etat === "echantillon" && (
          <Confirmer titre="Annuler l'échantillon ?" texte="Les fiches de l'échantillon partent à la corbeille (restaurables 30 jours)." testId="migration-cancel-sample"
            onOk={() => lancer("cancel-sample", "/migration-nocrm/annuler-echantillon", {})}><RotateCcw size={16} />Annuler l'échantillon</Confirmer>
        )}
      </Etape>
      <Etape n="3" titre="Import complet" testId="migration-step-3">
        <p className="text-xs at-muted">Relançable sans doublon : chaque fiche garde son identifiant noCRM. Les fiches déjà importées sont mises à jour (étape et agent conservés) et seuls les nouveaux commentaires sont ajoutés.</p>
        <Bouton cle="full" url="/migration-nocrm/complet" ok="Import complet terminé." disabled={bloque || e.etat === "a_configurer"}>{e.etat === "complet" ? "Relancer l'import" : "Lancer l'import complet"}<ArrowRight size={16} /></Bouton>
        {e.etat === "complet" && <Bouton cle="simulate" url="/migration-nocrm/simuler-activite" variante="at-btn-ghost">Simuler de nouveaux commentaires dans noCRM</Bouton>}
        {e.importees > 0 && (
          <Confirmer titre="Annuler l'import complet ?" texte={`Les ${e.importees} fiches importées et leurs appels rattachés partent à la corbeille (restaurables 30 jours).`} testId="migration-cancel-full"
            onOk={() => lancer("cancel-full", "/migration-nocrm/annuler-import", {})} danger><RotateCcw size={16} />Annuler l'import complet</Confirmer>
        )}
      </Etape>
      {e.rapport ? <Rapport r={e.rapport} /> : (
        <p className="text-sm at-muted flex items-center gap-2" data-testid="migration-no-report"><CheckCircle2 size={16} />Le rapport de contrôle apparaîtra après le premier import.</p>
      )}
    </div>
  );
}
